import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Package, BookOpen, ShoppingCart, Plus, FileText, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { getProducts, addToCart } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const Products = () => {
  const { category } = useParams();
  const { user } = useAuth();
  const { refreshCart } = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingToCart, setAddingToCart] = useState(null);

  const categoryTitle = category === 'ebooks' ? 'eBooks & Resources' : 'Professional Souvenirs';
  const categoryDescription = category === 'ebooks'
    ? 'Comprehensive guides, templates, and resources for legal, policy, and governance professionals'
    : 'Premium stationery, corporate gifts, and elegant office essentials for professionals';
  const categoryIcon = category === 'ebooks' ? <BookOpen className="h-16 w-16" /> : <Package className="h-16 w-16" />;

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        // Map URL category (plural) to database category (singular)
        const dbCategory = category === 'ebooks' ? 'ebook' : 'souvenir';
        const response = await getProducts(dbCategory);
        setProducts(response.data.filter(p => p.category === dbCategory));
      } catch (error) {
        console.error('Failed to fetch products:', error);
        toast.error('Failed to load products');
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [category]);

  const handleAddToCart = async (productId) => {
    if (!user) {
      toast.error('Please login to add items to cart');
      return;
    }

    setAddingToCart(productId);
    try {
      await addToCart({ product_id: productId, quantity: 1 });
      await refreshCart();
      toast.success('Added to cart!');
    } catch (error) {
      console.error('Failed to add to cart:', error);
      toast.error(error.response?.data?.detail || 'Failed to add to cart');
    } finally {
      setAddingToCart(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F9F7] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#8B1818]"></div>
      </div>
    );
  }

  // Show coming soon if no products
  if (products.length === 0) {
    return (
      <div className="min-h-screen bg-[#F9F9F7]">
        {/* Header */}
        <div className="py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
              {categoryTitle}
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {categoryDescription}
            </p>
          </div>
        </div>

        {/* Coming Soon Section */}
        <section className="py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-12 text-center">
              <div className="flex justify-center mb-6 text-[#8B1818]">
                {categoryIcon}
              </div>
              <div className="inline-flex items-center justify-center bg-blue-50 border border-blue-200 rounded-full px-4 py-2 mb-6">
                <Info className="h-5 w-5 text-blue-600 mr-2" />
                <span className="text-blue-900 font-semibold text-sm">COMING SOON</span>
              </div>
              <h2 className="text-3xl font-bold text-slate-900 mb-4">
                We're Preparing Something Special
              </h2>
              <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
                Our {category === 'ebooks' ? 'comprehensive eBooks and educational resources' : 'professional souvenirs and office essentials'} are currently being curated to provide you with the best quality materials. Check back soon for our full collection!
              </p>
              <div className="bg-slate-50 rounded-xl p-6 max-w-xl mx-auto">
                <h3 className="text-lg font-bold text-slate-900 mb-3">Stay Updated</h3>
                <p className="text-slate-600 mb-4">
                  Want to be notified when our products launch? Contact us at:
                </p>
                <a 
                  href="mailto:admin@lexdoright.com"
                  className="inline-flex items-center justify-center bg-[#8B1818] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#701313] transition-colors"
                >
                  admin@lexdoright.com
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      {/* Header */}
      <div className="py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
            {categoryTitle}
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            {categoryDescription}
          </p>
        </div>
      </div>

      {/* Products Grid */}
      <section className="py-8 pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product) => (
              <div 
                key={product.id} 
                className="bg-white rounded-2xl shadow-md border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow"
                data-testid={`product-${product.id}`}
              >
                {/* Product Image */}
                <div className="h-48 bg-gradient-to-br from-[#8B1818]/10 to-[#FDB913]/10 flex items-center justify-center relative">
                  {product.image_url ? (
                    <img 
                      src={product.image_url} 
                      alt={product.name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="text-center">
                      {product.category === 'ebook' ? (
                        <FileText className="h-16 w-16 text-[#8B1818]/40 mx-auto" />
                      ) : (
                        <Package className="h-16 w-16 text-[#8B1818]/40 mx-auto" />
                      )}
                    </div>
                  )}
                  {/* Category Badge */}
                  <span className="absolute top-3 right-3 px-3 py-1 bg-white/90 text-xs font-semibold text-slate-700 rounded-full shadow-sm">
                    {product.category === 'ebook' ? 'Digital' : 'Physical'}
                  </span>
                </div>

                {/* Product Info */}
                <div className="p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-2 line-clamp-2">
                    {product.name}
                  </h3>
                  <p className="text-slate-600 text-sm mb-4 line-clamp-3">
                    {product.description}
                  </p>

                  {/* Price & Stock */}
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-2xl font-bold text-[#8B1818]">
                      ₱{product.price?.toFixed(2)}
                    </span>
                    {product.category === 'souvenir' && (
                      <span className="text-sm text-slate-500">
                        {product.stock > 0 ? `${product.stock} in stock` : 'Out of stock'}
                      </span>
                    )}
                  </div>

                  {/* Add to Cart Button */}
                  <Button
                    onClick={() => handleAddToCart(product.id)}
                    disabled={addingToCart === product.id || (product.category === 'souvenir' && product.stock === 0)}
                    className="w-full bg-[#8B1818] text-white hover:bg-[#701313] disabled:opacity-50"
                    data-testid={`add-to-cart-${product.id}`}
                  >
                    {addingToCart === product.id ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/20 border-t-white mr-2"></div>
                        Adding...
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="h-4 w-4 mr-2" />
                        Add to Cart
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* Cart Link */}
          {user && (
            <div className="mt-12 text-center">
              <Link to="/cart">
                <Button variant="outline" className="border-[#8B1818] text-[#8B1818] hover:bg-[#8B1818]/5">
                  <ShoppingCart className="h-4 w-4 mr-2" />
                  View Cart
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Products;
